import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal, flushSync } from 'react-dom';
import { AnimatePresence, MotionConfig, motion } from 'motion/react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { TFunction } from 'i18next';
import {
  addCreatedGeneration,
  applyReleasedGenerations,
  readGenerationSummary,
  refreshGenerationSoon,
  useGenerationWatch,
  type GenerationPrefill,
} from '@entities/Generation';
import { moduleKeys, type UserModulesPage } from '@entities/Module';
import { planKeys, useQuotaQuery } from '@entities/Plan';
import { profileKeys } from '@entities/Profile';
import { useAuthStore } from '@entities/User';
import {
  ChooseTranslations,
  createButtonLabel,
  forgetLookup,
  useChoicePrice,
  useSenseChoice,
  useTranslationLookup,
  type CreateCardError,
  type InitialPicks,
  type LookupAnswer,
  type LookupResult,
} from '@features/CardsWorkspace';
import { useModuleCreatorStore } from '@features/CreateModule';
import { startToastBatch } from '@features/Notifications';
import { giftOffer, useSocialRewardController } from '@features/SocialReward';
import { trackEvent } from '@shared/analytics';
import { paths } from '@shared/config';
import { cn, useKeyboardInset } from '@shared/lib';
import { ChevronRightIcon, LockIcon, PlainField, PlusIcon, SearchIcon, TokenIcon } from '@shared/ui';
import type { ModuleSummary } from '@shared/types';
import {
  canEnterCreate,
  composerErrorEvent,
  createButtonKind,
  hasCyrillic,
  lookupErrorView,
  unbreakableModuleName,
} from './lib/composerModel';
import { markFirstDone, readFirstDone } from './lib/composerStorage';
import { useMediaQuery } from './lib/useMediaQuery';
import { useComposerLayers } from './model/useComposerLayers';
import { useComposerModule, type ComposerModule } from './model/useComposerModule';
import { useComposerSignal } from './model/useComposerSignal';
import { useCreateFlow, type CreatedPress, type CreatePress, type FlowNotice } from './model/useCreateFlow';
import { usePreviewRoom } from './model/usePreviewRoom';
import { useSheetMetrics } from './model/useSheetMetrics';
import { ComposerSignal } from './ui/ComposerSignal';
import { DictionarySheet, type SheetNotice, type SheetView } from './ui/DictionarySheet';
import { ManualChip, PencilIcon } from './ui/ManualChip';
import { ModuleChip } from './ui/ModuleChip';
import { ModuleMenu } from './ui/ModuleMenu';
import { ModuleSlot } from './ui/ModuleSlot';
import { PhonePreview } from './ui/PhonePreview';
import './HomeComposer.css';

/** Общий запрос модулей главной (ТЗ § 9): один на строки модулей и на чип. */
export interface ComposerModulesQuery {
  data: UserModulesPage | undefined;
  isError: boolean;
  refetch: () => unknown;
}

export interface HomeComposerProps {
  /** Телефонная раскладка (страница берёт её из useIsSidebarMobile: виджет Sidebar сюда не импортируют). */
  isMobile: boolean;
  /** Обёртка блока на месте тренировки: верх листа — не выше её низа + 24 px (ТЗ § 6). */
  heroRef: RefObject<HTMLElement>;
  /** Корень полосы (`data-dock`): по нему список «Генераций» меряет её высоту (ТЗ § 8). */
  dockRef: RefObject<HTMLElement>;
  modules: ComposerModulesQuery;
  /** Идёт подсветка после знакомства: строка видна, но `inert`. */
  tourActive: boolean;
  /** Телефон: открыт ли ряд перевода ручной карточки. Он растёт над строкой на 56 px, и пока он открыт,
   *  страница добавляет колонке столько же места снизу (ТЗ § 5, «Длинные наложения»). */
  onManualRowChange?: (open: boolean) => void;
  /**
   * «Ввести снова» и «Добавить вручную» из записи «Недавно добавленных» (ТЗ § 22.6): слово и модуль
   * записи встают в строку — с отмеченными недобравшимися значениями или в режиме ручной карточки.
   * Виджет применяет его и сразу вызывает `onPrefillDone` (страница сбрасывает проп).
   */
  prefill?: GenerationPrefill | null;
  onPrefillDone?: () => void;
  /** Страница закрыла полосу своим слоем (лист «Ещё» на телефоне): итог под ним не принимается, сигнал
   *  прозрачен (ТЗ § 8). Виджет `Sidebar` сюда не импортируют — признак даёт страница. */
  coveredByPage?: boolean;
  /** Заголовок над строкой — «Создать карточку» на «Словах и модулях» (владелец 2026-10-23: «что ТУТ создаётся
   *  карточка»). Прячется, пока строка в работе (над ней облачко, лист или клавиатура), но место держит. */
  heading?: string;
  /** Ученик сам сменил модуль в чипе или создал новый — страница выбирает его в списке модулей (2026-10-28: модуль
   *  строки и выбранный модуль — одно и то же). Модуль по умолчанию сюда не приходит. */
  onModuleChange?: (module: ComposerModule) => void;
  /** top — строка под заголовком страницы, липкая к верху; лист, превью и сигнал раскрываются вниз (владелец
   *  2026-10-28: «снизу не особо удобно… давай наверх, чтобы не скроллилась, а при открытии была сверху всех и
   *  затемнялась»). bottom — прежняя полоса внизу. */
  placement?: 'top' | 'bottom';
}

export interface HomeComposerHandle {
  /** «+ Карточка» с другой страницы (`focusComposer`): каретка в поле, а на телефоне — вспышка поля
   *  (программный фокус клавиатуру iOS не открывает), если строка открыта. */
  focusField: () => void;
  /** «Создать модуль» в пустом состоянии — как «+ Новый модуль» в чипе: модуль выбирается здесь. */
  createModule: () => void;
  /** Модуль выбрали в списке модулей страницы — карточки создаются в него; набранное слово не трогаем. */
  selectModule: (module: ComposerModule) => void;
}

type LocalNotice = { kind: 'moduleGone'; title: string } | { kind: 'modulesFailed' };

/** Телефон: сигнал у строки — не раньше 120 мс после ухода этапа 2 (и других слоёв), ТЗ § 13. */
const PHONE_LAYER_SETTLE_MS = 120;

function dailyLimitText(t: TFunction, error: CreateCardError): string {
  const quota = error.quota;
  if (!quota) return t('cardsWorkspace.errors.dailyLimitDetailed');
  const time = quota.resetAt
    ? new Date(quota.resetAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    : '00:00';
  return t('cardsWorkspace.errors.dailyLimitQuota', { used: quota.used, limit: quota.limit, time });
}

/** Четырёхлучевая звезда пилюли «Подобрать 10–20 слов на тему» — по образцу владельца (2026-10-21). */
function TopicStarIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinejoin="round"
      aria-hidden
      className={className}
    >
      <path d="M12 3.5c.7 4.6 3.4 7.3 8 8-4.6.7-7.3 3.4-8 8-.7-4.6-3.4-7.3-8-8 4.6-.7 7.3-3.4 8-8z" />
    </svg>
  );
}

/** Текст ошибки создания над кнопкой (ТЗ § 10): по существу, общее «не удалось» — только для неизвестного.
 *  `manual` — нажатие ручной карточки: у её 409 «слово ещё создаётся» свой текст (ТЗ § 23.1). */
function failedText(t: TFunction, error: CreateCardError, goneTitle: string, manual: boolean): string {
  switch (error.code) {
    case 'ALREADY_ADDED':
      return error.modules.length
        ? t('cardsWorkspace.errors.alreadyAddedIn', {
            count: error.modules.length,
            names: error.modules.map((m) => `«${m.name}»`).join(', '),
          })
        : t('cardsWorkspace.errors.alreadyAdded');
    case 'DAILY_LIMIT_REACHED':
      return dailyLimitText(t, error);
    case 'NOT_IN_DICTIONARY':
      return t('cardsWorkspace.errors.notInDictionary');
    case 'GENERATION_IN_PROGRESS':
      return manual ? t('cardsWorkspace.errors.generationInProgressManual') : t('cardsWorkspace.errors.generationInProgress');
    case 'MODULE_NOT_FOUND':
      return goneTitle ? t('homeComposer.errors.moduleGone', { title: goneTitle }) : t('cardsWorkspace.errors.moduleNotFound');
    case 'EMAIL_NOT_VERIFIED':
      return t('cardsWorkspace.errors.emailNotVerified');
    case 'RATE_LIMIT':
      return t('cardsWorkspace.errors.rateLimit');
    default:
      return t('cardsWorkspace.errors.unknownCreate');
  }
}

function flowNoticeView(t: TFunction, notice: FlowNotice | null, goneTitle: string, manual: boolean): SheetNotice | null {
  if (!notice) return null;
  switch (notice.kind) {
    case 'checking':
      return { tone: 'muted', text: t('homeComposer.errors.checking') };
    case 'checkOffline':
      return { tone: 'muted', text: t('homeComposer.errors.checkFailed') };
    case 'notCreated':
      return { tone: 'danger', text: t('homeComposer.errors.notCreated') };
    case 'notConfirmed':
      return { tone: 'danger', text: t('homeComposer.errors.notConfirmed') };
    case 'nothingAdded':
      return { tone: 'muted', text: t('homeComposer.errors.nothingAdded') };
    case 'failed':
      return { tone: 'danger', text: failedText(t, notice.error, goneTitle, manual) };
  }
}

function Spinner({ className }: { className?: string }) {
  return (
    <svg className={cn('animate-spin motion-reduce:animate-none', className)} viewBox="0 0 18 18" fill="none" aria-hidden>
      <circle cx="9" cy="9" r="7" stroke="currentColor" strokeOpacity="0.22" strokeWidth="2.2" />
      <path d="M16 9a7 7 0 0 0-7-7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" className={className} aria-hidden>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function ArrowUpIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M12 19V5M6 11l6-6 6 6" />
    </svg>
  );
}


/**
 * Строка создания карточки внизу главной (набросок D, ТЗ главной § 5–7, § 9–10, § 13): липкая полоса
 * постоянной высоты. Компьютер: слово → ответ словаря поднимает над строкой лист с выбором значений
 * (главная под ним затемнена 0,5), модуль — чип рядом с «Создать», цена — на кнопке. Телефон — два
 * этапа: превью главных переводов над строкой, затем страница выбора (`ChooseTranslations` порталом)
 * с модулем строкой над «Создать»; превью, этап 2 и меню модулей — записи в истории. Запись нажатия — в
 * секции «Недавно добавленные» под модулями (виджет GenerationsList); оттуда «Ввести снова» и «Добавить
 * вручную» приходят пропом `prefill` (§ 22.6). Пока строки записи нет на экране, над строкой — сигнал со
 * ссылкой к ней (§ 8, `ComposerSignal`); начало и итог нажатий объявляет скрытая live-область полосы.
 */
export const HomeComposer = forwardRef<HomeComposerHandle, HomeComposerProps>(function HomeComposer(
  {
    isMobile,
    heroRef,
    dockRef,
    modules: modulesQuery,
    tourActive,
    onManualRowChange,
    prefill,
    onPrefillDone,
    coveredByPage = false,
    heading,
    onModuleChange,
    placement = 'bottom',
  },
  ref,
) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const userId = user?.id;
  const openCreatorFor = useModuleCreatorStore((s) => s.openCreatorFor);
  const creatorOpen = useModuleCreatorStore((s) => s.open);
  const reward = useSocialRewardController();
  const surface = isMobile ? 'phone' : 'desktop';
  const atTop = placement === 'top';
  const fieldId = useId();
  const sheetId = useId();
  const menuId = useId();

  // --- Строка заблокирована, пока почта не подтверждена (ТЗ § 10). Доступ не кончается: Demo без срока с 2026-10-22.
  // Одна своя карточка — и без подтверждения (решение владельца 2026-11-02): пока она не потрачена, строка открыта.
  const locked: 'email' | null = user && !user.emailVerified && !user.guestCardAvailable ? 'email' : null;
  // Без почты генерация бесплатной карточки сорвалась уже после нажатия — сервер вернул попытку. Перечитать профиль,
  // чтобы строка открылась сама, а не после перезагрузки (тестировщик 2026-11-02).
  const failedPresses = useGenerationWatch(
    (s) => Object.values(s.entries).filter((entry) => entry.summary?.status === 'failed').length,
  );
  const unverified = !!user && !user.emailVerified;
  useEffect(() => {
    if (unverified && failedPresses > 0) void qc.invalidateQueries({ queryKey: profileKeys.current });
  }, [unverified, failedPresses, qc]);

  // --- Заход на главную: слежение за нажатиями этого захода (ТЗ § 8, «Данные»).
  useEffect(() => {
    useGenerationWatch.getState().enterHome();
    return () => useGenerationWatch.getState().leaveHome();
  }, []);

  // --- Поле, модуль, поиск.
  const [text, setText] = useState('');
  const word = text.trim();
  const fieldRef = useRef<HTMLDivElement>(null);
  const translationRef = useRef<HTMLDivElement>(null);
  const chipRef = useRef<HTMLButtonElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const quietFocus = useRef(false);
  const [focused, setFocused] = useState(false);
  const [translationFocused, setTranslationFocused] = useState(false);
  const editing = focused || translationFocused;
  // Телефон, строка сверху, в работе — строка поднята на верхнюю полосу (поверх логотипа); `liftTop` — липкий top
  // полосы, при котором карточка встаёт в 8 px от верха экрана (меряется при фокусе).
  const [liftTop, setLiftTop] = useState(-30);
  const coarse = useMediaQuery('(pointer: coarse)');
  // Подсказка в пустом поле телефона: до 640 px — короткая (ТЗ § 3).
  const wideField = useMediaQuery('(min-width: 640px)');
  // «Клавиатура открыта» — фокус в поле на сенсорном устройстве: по отступу клавиатуры не понять,
  // на Android он всегда 0 (ТЗ § 5).
  const keyboardOpen = editing && coarse;
  const inset = useKeyboardInset();

  const moduleList = modulesQuery.data?.items;
  const mod = useComposerModule({ userId, modules: moduleList, isError: modulesQuery.isError });
  // Меню модулей: на компьютере — попап (своё состояние), на телефоне — слой истории.
  const [menuOpen, setMenuOpen] = useState(false);
  const layers = useComposerLayers(isMobile && !locked);
  const previewOpen = layers.has('preview');
  const chooseOpen = layers.has('choose');
  const moduleMenuOpen = isMobile ? layers.has('modules') : menuOpen;
  const menuOpenRef = useRef(moduleMenuOpen);
  menuOpenRef.current = moduleMenuOpen;

  // Ручная карточка: слово → свой перевод (ТЗ § 6, § 7, «Ручная карточка»).
  const [manual, setManual] = useState<string | null>(null);
  // Ручной ввод открыт кнопкой «Вручную» (2026-10-21): он идёт за словом, а не закрывается при его правке.
  const manualPinned = useRef(false);
  useEffect(() => {
    if (manual === null) manualPinned.current = false;
  }, [manual]);
  const [translation, setTranslation] = useState('');
  // Сервер ответил на «Создать» 400 «нет в словаре» — лист показывает «Нет в словаре» с ручной
  // карточкой, пока слово то же (ТЗ § 10).
  const [notFoundFor, setNotFoundFor] = useState<string | null>(null);
  // «Добавить» привязал свою карточку слова из другого модуля, введённый перевод не применился (ТЗ § 22.6,
  // приёмка A2-03): облачко над пустой строкой, пока ученик не начал новое слово, не ушёл из поля и не нажал
  // что-то ещё. `module` — где слово уже было; сервер не сказал — `null`.
  const [reusedNote, setReusedNote] = useState<{ module: string | null } | null>(null);

  const cyrillic = hasCyrillic(word);
  // Модуля ещё нет (новое устройство, список грузится) — поиск ждёт список, чтобы не искать слово дважды.
  // В режиме ручной карточки поиск не выключаем: слово то же, ответ из кэша, а Esc вернёт к нему лист.
  const waitModules = mod.status === 'loading' && !mod.module;
  const lookup = useTranslationLookup(word, mod.module?.id ?? null, !locked && !cyrillic && !waitModules, {
    // На сенсорных экранах (и iPad) пауза дольше: меньше тратится общий лимит запросов (ТЗ § 7, § 18).
    debounceMs: coarse ? 400 : 250,
  });

  // --- Лист (компьютер) и превью (телефон): поднимаются только после ответа словаря на текущее слово
  // и модуль (ТЗ § 6, § 7).
  const [open, setOpen] = useState(false);
  // Ученик сам закрыл лист или превью: новый ответ на то же слово его не поднимает (вернёт правка слова,
  // фокус или нажатие в поле).
  const [collapsed, setCollapsed] = useState(false);
  const [shownAt, setShownAt] = useState<number | null>(null);
  const answer: LookupAnswer | null =
    lookup.status === 'found' || lookup.status === 'notFound' || lookup.status === 'error' ? lookup : null;
  const answerToken = answer
    ? answer.status === 'found'
      ? answer.data
      : `${answer.status}:${word.toLowerCase()}:${answer.status === 'error' ? answer.reason : ''}`
    : null;
  // Слово или модуль поменяли при открытом листе (превью, этапе 2) — прежний ответ остаётся,
  // приглушённый до 50%.
  const layerOpen = isMobile ? previewOpen || chooseOpen : open;
  const stale = lookup.status === 'loading' && layerOpen ? lookup.previous : null;
  const shown: LookupAnswer | null = answer ?? stale;
  const sheetVisible = !isMobile && !locked && (manual !== null || (open && shown !== null));
  const previewVisible = isMobile && !locked && manual === null && previewOpen && shown !== null;
  const phoneLifted = atTop && isMobile && (editing || previewVisible);
  const serverNotFound = notFoundFor !== null && notFoundFor === word.toLowerCase();
  const view: SheetView =
    manual !== null
      ? 'manual'
      : serverNotFound || shown?.status === 'notFound'
        ? 'notFound'
        : shown?.status === 'found'
          ? 'found'
          : 'error';

  const foundData = shown?.status === 'found' ? shown.data : null;
  // Этап 2 телефона держит выбор, пока ответ перечитывается для нового модуля или перечитать не
  // удалось: экран не пропадает из-под пальца.
  const lastFound = useRef<LookupResult | null>(null);
  if (foundData) lastFound.current = foundData;
  const choiceData = foundData ?? (isMobile && chooseOpen ? lastFound.current : null);
  // «Ввести снова» из записи: недобравшиеся значения отмечаются в первом ответе на это слово (ТЗ § 22.6).
  const [initialPicks, setInitialPicks] = useState<InitialPicks | null>(null);
  const choice = useSenseChoice(choiceData, { initialPicks });
  const cardCount = choice.cards.length;
  const price = useChoicePrice(choiceData ? cardCount : 0, choiceData ? choice.needsGeneration : false);
  // Жетоны закончились — и дневные, и несгораемые — строка в превью телефона (ТЗ § 7).
  const quota = useQuotaQuery().data;
  const quotaOut = !!quota && quota.remaining + quota.bonus <= 0;

  // Аналитика и тексты — по актуальным значениям, без перезапуска эффектов.
  const latest = useRef({ word, surface });
  latest.current = { word, surface };
  const answeredWords = useRef(new Set<string>());

  useEffect(() => {
    if (!answer) return;
    if (!collapsed) {
      if (isMobile) {
        // Превью — слой истории над строкой; поверх этапа 2 и меню модулей его не открываем.
        if (manual === null && !layers.has('choose') && !layers.has('modules')) layers.open('preview');
      } else {
        setOpen(true);
        setShownAt(answer.status === 'found' ? Date.now() : null);
      }
    }
    const key = latest.current.word.toLowerCase();
    if (!answeredWords.current.has(key)) {
      answeredWords.current.add(key);
      trackEvent('home_composer_answer_shown', {
        surface: latest.current.surface,
        result: answer.status === 'found' ? 'found' : answer.status === 'notFound' ? 'not_found' : 'error',
      });
    }
    if (answer.status === 'error') {
      trackEvent('home_composer_lookup_error', { surface: latest.current.surface, reason: answer.reason });
      // 403 при поиске — почта не подтверждена: перечитать профиль, строка станет «Почта не подтверждена».
      if (answer.reason === 'email') void qc.invalidateQueries({ queryKey: profileKeys.current });
    }
    // Только на новый ответ: `collapsed` и `answer` читаются из этого же рендера.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answerToken]);

  // Слово короче двух букв, кириллица или поиск выключен — лист и превью опускаются.
  useEffect(() => {
    if (lookup.status !== 'idle' || manual !== null) return;
    setOpen(false);
    setShownAt(null);
    if (isMobile) layers.close('preview');
    // layers.close стабилен.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookup.status, manual, isMobile]);

  // --- Слои телефона (ТЗ § 7).
  // Превью закрыли (касание затемнения, системная «Назад») — новый ответ на это слово его не поднимет.
  const prevPreviewOpen = useRef(previewOpen);
  useEffect(() => {
    if (prevPreviewOpen.current && !previewOpen && latest.current.word) setCollapsed(true);
    prevPreviewOpen.current = previewOpen;
  }, [previewOpen]);

  // Этап 2 или меню модулей закрылись без создания — снова этап 1 со словом: превью над строкой.
  const prevCovered = useRef(false);
  const covered = chooseOpen || layers.has('modules');
  useEffect(() => {
    const was = prevCovered.current;
    prevCovered.current = covered;
    if (!isMobile || !was || covered) return;
    if (answer && manual === null && !collapsed && latest.current.word) layers.open('preview');
    // Только на смену «закрыто/открыто».
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [covered]);

  // В истории слои, а показать нечего (перезагрузка — слово не хранится, «Назад» в старую запись).
  const layersStale = isMobile && ((previewOpen && (manual !== null || shown === null)) || (chooseOpen && choiceData === null));
  useEffect(() => {
    if (layersStale) layers.reset();
  }, [layersStale, layers.reset]);

  const metrics = useSheetMetrics(sheetVisible || previewVisible, { dock: dockRef, row: rowRef, hero: heroRef, below: atTop });
  const previewRoom = usePreviewRoom(previewVisible, rowRef, inset, atTop);

  // --- Создание.
  const [localNotice, setLocalNotice] = useState<LocalNotice | null>(null);
  const [verifying, setVerifying] = useState(false);
  const goneTitle = useRef('');

  /** Каретка в поле, в конец слова. `quiet` — без возврата листа и превью (после «Свернуть», Esc, создания). */
  const focusField = useCallback((quiet = false) => {
    const el = fieldRef.current;
    if (!el) return;
    if (document.activeElement !== el) {
      quietFocus.current = quiet;
      el.focus({ preventScroll: true });
    }
    if (el.textContent) {
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }, []);

  // Вспышка фона поля bg-elev → bg-card за 600 мс, рамка не меняется: «+ Карточка» с другой страницы на
  // телефоне, ручная карточка после этапа 2 (программный фокус клавиатуру iOS не открывает, ТЗ § 5).
  const [flashing, setFlashing] = useState(false);
  const flashField = useCallback(() => {
    setFlashing(false);
    window.requestAnimationFrame(() => setFlashing(true));
  }, []);
  useEffect(() => {
    if (!flashing) return;
    const timer = window.setTimeout(() => setFlashing(false), 650);
    return () => window.clearTimeout(timer);
  }, [flashing]);

  /** Успех: лист опускается, поле очищается (ТЗ § 6, § 7). Компьютер и ручная карточка телефона —
   *  фокус остаётся в поле; после этапа 2 — главная в покое, без клавиатуры. Если, пока шёл запрос, в
   *  поле набрали другое слово, его не стираем — уже ищется новое слово. */
  const resetAfterCreate = useCallback(
    (createdWord: string, keepFocus: boolean) => {
      setLocalNotice(null);
      if (latest.current.word.toLowerCase() !== createdWord.trim().toLowerCase()) return;
      setInitialPicks(null);
      setText('');
      setManual(null);
      setTranslation('');
      setOpen(false);
      setCollapsed(false);
      setShownAt(null);
      setMenuOpen(false);
      if (keepFocus) focusField(true);
      else fieldRef.current?.blur();
    },
    [focusField],
  );

  // Запись, из которой пришло «Добавить вручную» (§ 22.6): её упавший элемент сервер снимет — «удалена».
  const prefillSource = useRef<{ id: string; word: string } | null>(null);

  /**
   * Что нажатие меняет вне строки: модуль по умолчанию, запись и слежение, один итоговый тост на нажатие,
   * квота и списки модулей, «первый раз», аналитика. Делается и тогда, когда ученик ушёл с главной, пока шёл
   * запрос (`onCreatedAway`): ответ виджету уже не нужен, а без этого вместо одного тоста пришёл бы тост на
   * каждое слово и фразу, и квота осталась бы прежней (ревью этапа 4).
   */
  const applyCreated = (result: CreatedPress) => {
    const { press } = result;
    mod.remember({ id: press.args.moduleId, title: press.args.moduleTitle });
    if (result.generation) {
      // Запись встала в загруженный список — сигнал решает через 300 мс после вставки её строки;
      // вставлять некуда (список не загружен) — от ответа (ТЗ § 8). Сводка для слежения — свежайшая
      // известная: SSE о записи могло прийти раньше ответа.
      const placed = addCreatedGeneration(qc, result.generation, result.requestedAt);
      useGenerationWatch.getState().watch(
        readGenerationSummary(qc, result.generation.id) ?? result.generation,
        result.cards.map((c) => c.cardId),
        { noRow: !placed },
      );
    }
    // Ручная карточка сняла ожидание упавших карточек того же слова: их записи (элемент — «удалена») пришли в
    // ответе (`released`). Ответ о них не сказал (старый бэкенд, проверка после обрыва) — запись, из которой
    // пришло «Добавить вручную», один раз перечитывается; SSE `generationUpdated` её и так обновит.
    if (press.kind === 'manual' && result.released) applyReleasedGenerations(qc, result.released);
    const source = prefillSource.current;
    if (press.kind === 'manual' && source && source.word === press.args.front.trim().toLowerCase()) {
      prefillSource.current = null;
      if (!result.released) void refreshGenerationSoon(qc, source.id);
    }
    // Одно уведомление на нажатие: «готово» карточек группы соберётся в один тост (как на /cards/new).
    startToastBatch(result.cards.filter((c) => c.generating).map(({ cardId, front }) => ({ cardId, front })));
    void qc.invalidateQueries({ queryKey: moduleKeys.listsRoot });
    // Бесплатная карточка без подтверждения почты потрачена — профиль скажет, и строка закроется до подтверждения.
    if (user && !user.emailVerified) void qc.invalidateQueries({ queryKey: profileKeys.current });
    if (press.kind === 'cards') {
      void qc.invalidateQueries({ queryKey: planKeys.quota });
      void qc.invalidateQueries({ queryKey: planKeys.billing });
    }
    const first = !readFirstDone(userId);
    markFirstDone(userId);
    if (press.kind === 'cards') {
      trackEvent('home_cards_created', {
        surface,
        cards: press.meta.cards,
        picks: press.meta.picks,
        spent: press.meta.paid ? 1 : 0,
        module: press.meta.origin,
        first,
        via: press.meta.via,
      });
    } else {
      trackEvent('home_manual_card_added', { surface });
    }
  };

  const handleCreated = (result: CreatedPress) => {
    applyCreated(result);
    // Телефон после этапа 2: назад ровно на фактическую глубину слоёв — главная в покое (ТЗ § 7).
    if (isMobile) layers.closeAll();
    resetAfterCreate(result.press.args.front, !(isMobile && result.press.kind === 'cards'));
    // Строка уже сброшена, фокус в поле: переиспользованная карточка — облачко над строкой (A2-03).
    setReusedNote(result.reused ? { module: result.reused.moduleName } : null);
  };

  const openModuleMenu = () => {
    if (isMobile) layers.open('modules');
    else setMenuOpen(true);
  };

  const handleFailed = (error: CreateCardError) => {
    trackEvent('home_composer_error', { code: composerErrorEvent(error.code) });
    // Без почты: сорвавшееся нажатие бесплатную карточку не тратит — профиль скажет, открыта ли строка.
    if (user && !user.emailVerified) void qc.invalidateQueries({ queryKey: profileKeys.current });
    switch (error.code) {
      case 'MODULE_NOT_FOUND':
        if (mod.module) {
          goneTitle.current = mod.module.title;
          mod.forget(mod.module.id);
        }
        // Удалённый модуль ещё может быть в списке — перечитываем, чтобы чип его не вернул.
        void modulesQuery.refetch();
        openModuleMenu();
        break;
      case 'DAILY_LIMIT_REACHED':
        void qc.invalidateQueries({ queryKey: planKeys.quota });
        void qc.invalidateQueries({ queryKey: planKeys.billing });
        break;
      case 'EMAIL_NOT_VERIFIED':
        // Профиль уже перечитывается выше: строка станет «После подтверждения почты».
        break;
      case 'ALREADY_ADDED':
        // Метки «учишь» обновятся: ответ словаря перечитывается.
        forgetLookup(latest.current.word);
        break;
      case 'NOT_IN_DICTIONARY':
        // Лист (на телефоне — превью) переходит в «Нет в словаре» с ручной карточкой — отдельная строка
        // ошибки не нужна. На телефоне этап 2 закрывается: выбирать больше нечего.
        setNotFoundFor(latest.current.word.toLowerCase());
        flow.clearNotice();
        if (isMobile) layers.close('choose');
        break;
      default:
        break;
    }
  };

  const flow = useCreateFlow({
    onCreated: handleCreated,
    onCreatedAway: applyCreated,
    onFailed: handleFailed,
    onCheckResult: (result) => trackEvent('home_composer_check', { result }),
    // Ничего не создано — метки «учишь» обновятся: ответ словаря перечитывается (ТЗ § 8).
    onNothingAdded: (press) => forgetLookup(press.args.front),
  });
  const creating = flow.phase === 'creating' || verifying;
  const busy = flow.phase !== 'idle' || verifying;

  /** Сверка модуля со свежим списком перед запросом (ТЗ § 9); удалён — запроса нет, меню открывается. */
  const submitVerified = async (press: CreatePress, target: ComposerModule) => {
    setLocalNotice(null);
    setReusedNote(null);
    flow.clearNotice();
    setVerifying(true);
    const verdict = await mod.verify(target.id);
    setVerifying(false);
    if (verdict === 'gone') {
      goneTitle.current = target.title;
      setLocalNotice({ kind: 'moduleGone', title: target.title });
      mod.forget(target.id);
      void modulesQuery.refetch();
      openModuleMenu();
      trackEvent('home_composer_error', { code: 'MODULE_NOT_FOUND' });
      return;
    }
    if (verdict === 'failed') {
      setLocalNotice({ kind: 'modulesFailed' });
      return;
    }
    void flow.submit(press);
  };

  /** Модуль создаётся в модалке и сразу выбирается (решение владельца); `then` — продолжить нажатие. */
  const createModuleThen = (then?: (created: ComposerModule) => void) => {
    if (isMobile) layers.close('modules');
    else setMenuOpen(false);
    openCreatorFor((created: ModuleSummary) => {
      const next = { id: created.id, title: created.title };
      mod.select(next, 'new');
      mod.remember(next);
      onModuleChange?.(next);
      trackEvent('home_composer_module_changed', { from_recent: false, created: true });
      if (then) then(next);
      // На телефоне без нажатия в поле фокус клавиатуру не откроет — каретку не ставим.
      else if (!isMobile) window.setTimeout(() => focusField(true), 0);
    });
  };

  const pendingReread = lookup.status === 'loading' && stale !== null;
  const foundCurrent = answer?.status === 'found' && !serverNotFound;
  const canCreate = !busy && !pendingReread && foundCurrent && cardCount > 0 && !price.blocked;

  const buildCardsPress = (target: ComposerModule, via: 'enter' | 'click', origin = mod.origin): CreatePress => {
    const wordCard = choice.cards.find((c) => c.isWord);
    return {
      kind: 'cards',
      args: {
        front: word,
        moduleId: target.id,
        moduleTitle: target.title,
        difficulty: 'B1',
        picks: wordCard?.picks ?? [],
        phrases: choice.cards.filter((c) => !c.isWord).map((c) => ({ front: c.front, picks: c.picks })),
      },
      meta: { via, paid: price.paid, cards: cardCount, picks: choice.count, origin },
    };
  };

  const create = (via: 'enter' | 'click') => {
    if (!canCreate) return;
    const target = mod.module;
    // Модулей нет — модалка нового модуля, и карточки создаются тем же нажатием (ТЗ § 9).
    if (!target) {
      createModuleThen((created) => void flow.submit(buildCardsPress(created, via, 'new')));
      return;
    }
    void submitVerified(buildCardsPress(target, via), target);
  };

  const addManual = (via: 'enter' | 'click') => {
    const value = translation.trim();
    if (manual === null || busy) return;
    if (!word) {
      focusField(true);
      return;
    }
    if (!value) {
      translationRef.current?.focus();
      return;
    }
    // Телефон: фокус — в поле слова в том же нажатии. После успеха поле перевода закроется, а клавиатура
    // останется в поле слова (ТЗ § 7): вне нажатия iOS её уже не откроет.
    if (isMobile) focusField(true);
    const build = (target: ComposerModule, origin = mod.origin): CreatePress => ({
      kind: 'manual',
      args: { front: word, translations: [value], moduleId: target.id, moduleTitle: target.title, difficulty: 'B1' },
      meta: { via, paid: false, cards: 1, picks: 1, origin },
    });
    const target = mod.module;
    if (!target) {
      createModuleThen((created) => void flow.submit(build(created, 'new')));
      return;
    }
    void submitVerified(build(target), target);
  };

  // --- Лист: свернуть и вернуть без потери слова и отметок (ТЗ § 6); на телефоне — превью (§ 7).
  const collapse = () => {
    setCollapsed(true);
    setOpen(false);
    setMenuOpen(false);
    focusField(true);
  };

  /** Выйти из строки: фокус уходит из поля слова и перевода — гаснет затемнение по фокусу. */
  const leaveField = () => {
    fieldRef.current?.blur();
    translationRef.current?.blur();
  };

  /** Касание затемнения на телефоне: превью закрывается, слово остаётся, клавиатура прячется. */
  const closePreview = () => {
    layers.close('preview');
    fieldRef.current?.blur();
  };

  const reopen = () => {
    setCollapsed(false);
    if (!answer) return;
    if (isMobile) {
      if (manual === null && !previewOpen && !chooseOpen) layers.open('preview');
    } else if (!open) {
      setOpen(true);
      setShownAt(answer.status === 'found' ? Date.now() : null);
    }
  };

  // Этап 2 телефона (ТЗ § 7): плашка «Выбрать, что учить» или Enter. Клавиатуру прячем — экрану выбора
  // она не нужна. Слово шапки фиксируем: пока этап 2 уезжает после «Создать», поле уже пустое.
  const [chooseWord, setChooseWord] = useState('');
  const openChoose = (via: 'plaque' | 'enter') => {
    if (!foundCurrent || choice.allLocked || busy) return;
    setChooseWord(word);
    setCollapsed(false);
    fieldRef.current?.blur();
    layers.open('choose');
    trackEvent('home_composer_choose_opened', { via });
  };

  const enterManual = () => {
    if (isMobile) {
      layers.close('preview');
      // Фокус в поле перевода — в том же нажатии: иначе iPhone не откроет клавиатуру (ТЗ § 7).
      flushSync(() => {
        setManual(word.toLowerCase());
        setTranslation('');
        setCollapsed(false);
      });
      translationRef.current?.focus({ preventScroll: true });
    } else {
      setManual(word.toLowerCase());
      setTranslation('');
      setCollapsed(false);
      setMenuOpen(false);
    }
    flow.clearNotice();
    setLocalNotice(null);
  };

  /** «Добавить вручную» на этапе 2 (жетоны закончились): назад на главную, то же слово — ручной картой. Как
   *  `enterManual`: ряд перевода появляется и получает фокус в том же нажатии (на телефоне сразу клавиатура),
   *  этап 2 уезжает следом; вспышки поля слова нет (приёмка A1-09). «Назад» по истории — первым: пока он в пути,
   *  `layers.reset()` (слои при ручной карточке) ничего не заменяет, и история остаётся чистой. */
  const manualFromChoose = () => {
    layers.closeAll();
    flushSync(() => {
      setManual(word.toLowerCase());
      setTranslation('');
      setCollapsed(false);
    });
    translationRef.current?.focus({ preventScroll: true });
    flow.clearNotice();
    setLocalNotice(null);
  };

  /** «Вручную» в строке: ручной ввод сразу, даже без слова; повторное нажатие закрывает его. Без слова фокус —
   *  в поле слова в том же нажатии (на телефоне — клавиатура), со словом — в поле перевода, как `enterManual`. */
  const toggleManual = () => {
    if (manual !== null) {
      exitManual();
      return;
    }
    manualPinned.current = true;
    trackEvent('home_manual_opened', { surface });
    if (word) {
      enterManual();
      return;
    }
    if (isMobile) layers.close('preview');
    flushSync(() => {
      setManual('');
      setTranslation('');
      setCollapsed(false);
      setMenuOpen(false);
    });
    focusField(true);
    flow.clearNotice();
    setLocalNotice(null);
  };

  const exitManual = () => {
    setManual(null);
    setTranslation('');
    prefillSource.current = null;
    flow.clearNotice();
    setLocalNotice(null);
    focusField(true);
  };

  // Ручная карточка открылась на компьютере — фокус в поле перевода (на телефоне — в самом нажатии). Без слова
  // («Вручную» в пустой строке) фокус уже в поле слова.
  const manualOpen = manual !== null;
  useEffect(() => {
    if (manualOpen && !isMobile && word) translationRef.current?.focus({ preventScroll: true });
    // Только на открытие ручной карточки.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualOpen]);

  // Телефон: ряд перевода открыт — карточка строки выше на 56 px, странице нужен запас снизу (ТЗ § 5).
  // Это действие ученика, а не видимость: отступ не мигает у конца прокрутки.
  const manualRowOpen = isMobile && !locked && manual !== null;
  const manualRowChanged = useRef(onManualRowChange);
  manualRowChanged.current = onManualRowChange;
  useEffect(() => {
    manualRowChanged.current?.(manualRowOpen);
  }, [manualRowOpen]);

  const onTextChange = (value: string) => {
    setText(value);
    setCollapsed(false);
    setNotFoundFor(null);
    setReusedNote(null);
    // Отметки «Ввести снова» привязаны к слову записи: на другом слове они не применяются.
    const next = value.trim().toLowerCase();
    if (initialPicks && initialPicks.word.trim().toLowerCase() !== next) setInitialPicks(null);
    if (prefillSource.current && prefillSource.current.word !== next) prefillSource.current = null;
    // Правка слова отменяет ожидание проверки и убирает прежнюю строку над кнопкой.
    flow.cancel();
    flow.clearNotice();
    setLocalNotice(null);
    if (manual !== null && next !== manual) setManual(manualPinned.current ? next : null);
  };

  const clear = () => {
    setText('');
    setManual(null);
    setTranslation('');
    setInitialPicks(null);
    prefillSource.current = null;
    setNotFoundFor(null);
    setOpen(false);
    setCollapsed(false);
    if (isMobile) layers.close('preview');
    flow.cancel();
    flow.clearNotice();
    setLocalNotice(null);
    focusField(true);
  };

  const onFieldKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      if (manual !== null) {
        if (translation.trim()) addManual('enter');
        else translationRef.current?.focus();
        return;
      }
      // Телефон: Enter на клавиатуре («go») открывает этап 2, как плашка (ТЗ § 7).
      if (isMobile) {
        openChoose('enter');
        return;
      }
      // Лист свёрнут — Enter открывает его, но не создаёт.
      if (!sheetVisible) {
        reopen();
        return;
      }
      if (
        canEnterCreate({
          sheetOpen: sheetVisible,
          foundCurrent,
          selected: cardCount,
          shownAt,
          now: Date.now(),
          busy: busy || pendingReread,
          blocked: price.blocked,
        })
      ) {
        create('enter');
      }
    } else if (event.key === 'ArrowUp' && !isMobile && !sheetVisible && answer) {
      event.preventDefault();
      reopen();
    }
  };

  // Esc из поля и из листа сворачивает лист; при свёрнутом — уводит фокус из поля (гаснет затемнение), слово
  // остаётся (✕ очищает, Esc — нет). Телефон с клавиатурой: Esc закрывает превью.
  const onCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    if (manual !== null) {
      event.preventDefault();
      exitManual();
      return;
    }
    if (isMobile) {
      if (previewOpen) {
        event.preventDefault();
        layers.close('preview');
      }
      return;
    }
    event.preventDefault();
    if (sheetVisible) collapse();
    else leaveField();
  };

  // Клик по пустому месту строки — каретка в поле; клик в поле при свёрнутом листе возвращает лист.
  const onFieldAreaPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest('button') || translationRef.current?.contains(target)) return;
    if (!fieldRef.current?.contains(target)) {
      event.preventDefault();
      focusField();
    }
    reopen();
  };

  // Телефон: касание поля или пустого места ряда возвращает превью; «+» и пустое место ставят фокус в
  // поле в том же касании — клавиатура откроется.
  const onPhoneRowPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('button')) return;
    reopen();
  };
  const onPhoneRowClick = (event: { target: EventTarget }) => {
    const target = event.target as HTMLElement;
    if (target.closest('button') || fieldRef.current?.contains(target)) return;
    fieldRef.current?.focus({ preventScroll: true });
  };

  // Клик по затемнению сворачивает лист; если в этот момент было открыто меню — закрывается только меню.
  // Меню закрывается «кликом вне» ещё на захвате pointerdown и может успеть перерисоваться раньше
  // обработчика затемнения, поэтому признак держим до конца события (таймер 0).
  const dimDownWithMenu = useRef(false);
  const menuClosedOutside = useRef(false);
  const closeMenu = (restoreFocus: boolean) => {
    if (isMobile) {
      layers.close('modules');
      return;
    }
    setMenuOpen(false);
    if (restoreFocus) {
      chipRef.current?.focus({ preventScroll: true });
      return;
    }
    menuClosedOutside.current = true;
    window.setTimeout(() => {
      menuClosedOutside.current = false;
    }, 0);
  };

  const onSelectModule = (picked: ModuleSummary, fromRecent: boolean) => {
    if (isMobile) {
      layers.close('modules');
    } else {
      setMenuOpen(false);
      chipRef.current?.focus({ preventScroll: true });
    }
    if (picked.id === mod.module?.id) return;
    mod.select({ id: picked.id, title: picked.title }, 'changed');
    setLocalNotice(null);
    setReusedNote(null);
    flow.clearNotice();
    onModuleChange?.({ id: picked.id, title: picked.title });
    trackEvent('home_composer_module_changed', { from_recent: fromRecent, created: false });
  };

  // --- Подсветка: строка видна, но не нажимается и фокус в неё не ставится.
  useEffect(() => {
    const dock = dockRef.current;
    if (dock) dock.inert = tourActive;
  }, [dockRef, tourActive]);

  useImperativeHandle(
    ref,
    () => ({
      focusField: () => {
        if (locked || tourActive) return;
        if (isMobile) flashField();
        else focusField();
      },
      createModule: () => createModuleThen(),
      selectModule: (next: ComposerModule) => {
        if (next.id === mod.module?.id) return;
        mod.select(next, 'changed');
        setLocalNotice(null);
        setReusedNote(null);
        flow.clearNotice();
      },
    }),
    // createModuleThen читает актуальный модуль через замыкание рендера.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locked, tourActive, isMobile, focusField, flashField, mod.select, mod.remember, mod.module?.id],
  );

  // --- «Ввести снова» и «Добавить вручную» из записи «Недавно добавленных» (ТЗ § 22.6). Применяем один
  // раз на каждый новый объект `prefill` и сразу сообщаем странице — она сбрасывает проп.
  const prefillDone = useRef(onPrefillDone);
  prefillDone.current = onPrefillDone;
  const moduleChanged = useRef(onModuleChange);
  moduleChanged.current = onModuleChange;
  useEffect(() => {
    if (!prefill) return;
    const finish = () => prefillDone.current?.();
    const word = prefill.word.trim();
    // Строка заблокирована (почта, доступ) или идёт подсветка — кнопок в записи нет; на всякий случай ничего.
    if (locked || tourActive) {
      finish();
      return;
    }
    // Модуль записи — если он есть в свежем списке модулей; иначе остаётся модуль строки.
    const target = prefill.moduleId ? moduleList?.find((m) => m.id === prefill.moduleId) : undefined;
    if (target && target.id !== mod.module?.id) {
      mod.select({ id: target.id, title: target.title }, 'changed');
      // Модуль записи или страницы модуля — выбран и в списке модулей страницы (2026-10-28).
      moduleChanged.current?.({ id: target.id, title: target.title });
    }
    if (!word) {
      // Только модуль — «Добавить карточку» со страницы модуля и новый модуль (страницы «Из словаря» больше нет,
      // решение владельца 2026-10-21): модуль выбран, каретка в поле.
      if (isMobile) flashField();
      else window.setTimeout(() => focusField(true), 0);
      finish();
      return;
    }
    const sameWord = word.toLowerCase() === latest.current.word.toLowerCase();
    setMenuOpen(false);
    flow.cancel();
    flow.clearNotice();
    setLocalNotice(null);
    setReusedNote(null);
    setNotFoundFor(null);
    setCollapsed(false);
    setText(word);
    if (prefill.mode === 'manual') {
      // Отказ модерации: то же слово ручной карточкой, без жетона. Ответ словаря режим не подменяет —
      // Esc и «⌄» вернут к нему (ТЗ § 23.4, п. 6).
      setInitialPicks(null);
      setManual(word.toLowerCase());
      setTranslation('');
      prefillSource.current = prefill.sourceId ? { id: prefill.sourceId, word: word.toLowerCase() } : null;
      // Телефон: ряд перевода открыт, поле вспыхивает — программный фокус клавиатуру iOS не открывает.
      if (isMobile) flashField();
      else window.setTimeout(() => translationRef.current?.focus({ preventScroll: true }), 0);
    } else {
      // «Ввести снова»: поиск идёт сразу, недобравшиеся значения отмечены в первом ответе на это слово.
      setManual(null);
      setTranslation('');
      prefillSource.current = null;
      setInitialPicks(prefill.picks?.length ? { word, picks: prefill.picks } : null);
      // Телефон: поле заполняется без клавиатуры, превью поднимется с ответом словаря.
      if (!isMobile) window.setTimeout(() => focusField(true), 0);
      // Ответ на это слово уже на экране — новый ответ не придёт, лист (превью) поднимаем сами.
      if (sameWord) reopen();
    }
    finish();
    // Только на новый объект prefill: остальное читается из этого рендера.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  // --- Облачко у строки (ТЗ § 10): кириллица; на телефоне ещё медленная сеть и заметка ручной карточки. Подсказку
  // первого раза («Перевод и транскрипцию найдём в словаре…») владелец убрал 2026-10-28 — её говорит сама подсказка
  // поля «Найти в словаре».
  // Переиспользованная карточка (A2-03) — над пустой строкой, пока ученик не начал новое слово.
  const reusedText =
    reusedNote && !text
      ? reusedNote.module
        ? t('homeComposer.manualReused', { module: unbreakableModuleName(reusedNote.module) })
        : t('homeComposer.manualReusedElsewhere')
      : null;
  const cloud =
    locked || sheetVisible
      ? null
      : cyrillic
        ? t('homeComposer.cyrillic')
        : reusedText;

  // --- Строка над кнопкой.
  const notice: SheetNotice | null = localNotice
    ? localNotice.kind === 'moduleGone'
      ? { tone: 'danger', text: t('homeComposer.errors.moduleGone', { title: localNotice.title }) }
      : { tone: 'danger', text: t('homeComposer.errors.modulesFailed') }
    : flowNoticeView(t, flow.notice, goneTitle.current, manual !== null);

  const phoneCloud: SheetNotice | null =
    !isMobile || locked || previewVisible || chooseOpen
      ? null
      : manual !== null
        ? (notice ??
          (mod.module
            ? { tone: 'muted', text: t('homeComposer.manualNote', { module: unbreakableModuleName(mod.module.title) }) }
            : null))
        : cyrillic
          ? { tone: 'muted', text: t('homeComposer.cyrillic') }
          : reusedText
            ? { tone: 'muted', text: reusedText }
            : lookup.status === 'loading' && lookup.slow
              ? { tone: 'muted', text: t('homeComposer.slow') }
              : null;

  // Жетоны закончились — под составом выходы без жетона и подарок (ТЗ § 10).
  const offer = reward ? giftOffer(reward) : null;
  const exhaustedActions = (onManual: () => void): ReactNode =>
    price.blocked ? (
      <div className="flex flex-wrap items-center gap-x-4">
        <button
          type="button"
          onClick={onManual}
          className="min-h-11 text-[14px] font-medium text-fg underline-offset-4 hover:underline"
        >
          {t('cardsWorkspace.pick.addManual')}
        </button>
        {offer && reward && (
          <button
            type="button"
            onClick={() => {
              trackEvent('social_reward_opened', { place: 'composer' });
              reward.show();
            }}
            className="min-h-11 text-[14px] font-medium text-fg underline-offset-4 hover:underline"
          >
            {t('homeComposer.refill')}
          </button>
        )}
        <Link
          to={paths.plans}
          className="inline-flex min-h-11 items-center text-[14px] font-medium text-fg underline-offset-4 hover:underline"
        >
          {t('nav.plans')}
        </Link>
      </div>
    ) : undefined;

  // --- Кнопка «Создать» существует только вместе с листом (ТЗ § 6).
  const showCreate = sheetVisible && (view === 'found' || view === 'manual');
  const manualReady = manual !== null && word.length > 0 && translation.trim().length > 0 && !busy;
  const enabled = view === 'manual' ? manualReady : canCreate;
  // «Проверяем…» — неизвестный исход нажатия (ТЗ § 10), в том числе у ручной карточки: без ⏎.
  const flowChecking = flow.phase === 'checking';
  const buttonKind = createButtonKind({
    manual: view === 'manual',
    checkingOutcome: flowChecking,
    rereading: pendingReread,
    creating,
    blocked: price.blocked,
    allLocked: choice.allLocked,
    cards: cardCount,
  });
  let wideLabel: string;
  let shortLabel: string;
  let actionable = false;
  switch (buttonKind) {
    case 'checking':
      wideLabel = shortLabel = t('cardsWorkspace.choose.checking');
      break;
    case 'manual':
      wideLabel = t('homeComposer.addCard');
      shortLabel = t('homeComposer.addShort');
      actionable = true;
      break;
    case 'exhausted':
      wideLabel = shortLabel = t('cardsWorkspace.choose.costExhausted');
      break;
    case 'allAdded':
      wideLabel = shortLabel = t('cardsWorkspace.choose.allAdded');
      break;
    case 'pickSense':
      wideLabel = shortLabel = t('cardsWorkspace.pick.pickSense');
      break;
    default:
      wideLabel = createButtonLabel(t, cardCount, price);
      shortLabel = price.paid ? t('homeComposer.createShortPaid') : t('homeComposer.createShort');
      actionable = true;
  }

  const statusText =
    lookup.status === 'loading' && !sheetVisible
      ? lookup.slow
        ? t('homeComposer.slow')
        : t('cardsWorkspace.pick.searching')
      : null;
  const topicsHref = mod.module ? `${paths.topicCards}?moduleId=${encodeURIComponent(mod.module.id)}` : paths.topicCards;

  // Почта не подтверждена — строка видна, но неактивна, с подписью «Откроется после подтверждения почты»;
  // просьба о почте — плашкой вверху главной (решение владельца 2026-10-21).
  const emailLocked = locked === 'email';

  // --- Телефон: превью, этап 2.
  const previewAnswer: LookupAnswer | null = serverNotFound ? { status: 'notFound', frontName: word.toLowerCase() } : shown;
  const previewError = previewAnswer?.status === 'error' ? lookupErrorView(previewAnswer.reason) : null;
  // Этап 2 (ТЗ § 23.3): «Проверяем…» — только пока ответ перечитывается или проверяется исход нажатия.
  // Перечитать не удалось — причина и «Повторить» у строки, а кнопка неактивна со своей подписью.
  const choosePending = flow.phase === 'checking' || pendingReread;
  const chooseCreateDisabled = !foundCurrent && !pendingReread;
  const rereadError = answer?.status === 'error' ? lookupErrorView(answer.reason) : null;
  const chooseNotice: SheetNotice | null = notice ?? (rereadError ? { tone: 'muted', text: t(rereadError.key) } : null);
  const chooseNoticeAction =
    !notice && rereadError?.retry ? { label: t('training.retry'), onClick: lookup.retry } : undefined;
  const chooseVisible = isMobile && !locked && chooseOpen && choiceData !== null;

  const dimOn = sheetVisible || previewVisible;
  // Поле в фокусе — главная под строкой тоже затемнена (решение владельца 2026-10-22: «когда инпут в фокусе, затемняй
  // все»). Только вид: сигнал у строки и приём итога генерации идут по `dimOn` (листа нет — место над строкой свободно).
  const focusDim = editing && !locked;

  // --- Сигнал у строки (ТЗ § 8, § 23.4 п. 5 и 7). Слои и клавиатура — в стор слежения: при них сигнал
  // прозрачен, а итог не принимается. Облачко над строкой (любое, включая заметку и ошибку ручной
  // карточки), подсветка и заблокированная строка тоже прячут сигнал — место над строкой одно. Слои вне
  // строки, которые закрывают полосу и главную, — тоже: модалка нового модуля, диалог подарка, подсветка
  // после знакомства и выдвижное меню телефона (`coveredByPage` от страницы). Иначе «Готово» отсчитывало бы 6 с,
  // а запись «была на экране», пока их никто не видел.
  const overlayOpen =
    dimOn || chooseOpen || moduleMenuOpen || creatorOpen || !!reward?.open || tourActive || coveredByPage;
  useEffect(() => {
    useGenerationWatch.getState().setOverlay(overlayOpen, isMobile ? PHONE_LAYER_SETTLE_MS : 0);
  }, [overlayOpen, isMobile]);
  useEffect(() => {
    useGenerationWatch.getState().setKeyboard(keyboardOpen);
  }, [keyboardOpen]);
  const signal = useComposerSignal({
    blocked: locked !== null || tourActive || (isMobile ? phoneCloud !== null : cloud !== null),
    surface,
    // Убрали ✕ — на компьютере фокус возвращается в поле (без листа); на телефоне клавиатуру не открываем.
    onDismissed: () => {
      if (!isMobile) focusField(true);
    },
  });
  const signalNode = (
    <ComposerSignal
      view={signal.view}
      phone={isMobile}
      below={atTop}
      onShow={signal.show}
      onDismiss={signal.dismiss}
      onHold={signal.hold}
    />
  );

  // Строка по макету B (решение владельца 2026-10-21): сверху поле, снизу чип модуля, «Вручную» и кнопка справа —
  // со словом стрелка, в пустом поле она серая. Подбор слов по теме — пилюлей «✦ Подобрать 10–20 слов на тему ›»
  // под строкой (решение владельца 2026-10-21, заменила строку «Хочешь сразу 10–20 слов на тему? Подбор слов по
  // теме →»). Рамка при вводе — спокойная: оранжевая «страшно смотрится» (решение владельца 2026-10-21).
  const topicsPillInner = (
    <>
      <TopicStarIcon className={cn('h-4 w-4 flex-none', emailLocked ? 'text-muted' : 'text-accent')} />
      <span className="min-w-0 truncate">{t('homeComposer.topicPill')}</span>
      <ChevronRightIcon className="h-3.5 w-3.5 flex-none text-[#8a8a90]" aria-hidden />
    </>
  );
  const topicsPillClass =
    'inline-flex h-9 max-w-full items-center gap-2 rounded-pill border border-solid border-line bg-card pl-3 pr-2.5 text-[14px] font-medium';
  const topicsLink = emailLocked ? (
    <span className={cn(topicsPillClass, 'text-muted')}>{topicsPillInner}</span>
  ) : (
    <Link
      to={topicsHref}
      data-tour="topics"
      onClick={() => trackEvent('home_topics_link_clicked', { surface })}
      className={cn(topicsPillClass, 'text-fg transition-colors hover:bg-elev active:bg-elev')}
    >
      {topicsPillInner}
    </Link>
  );
  const arrowButton = ({
    onClick,
    ready,
    spinning,
    label,
  }: {
    onClick: () => void;
    ready: boolean;
    spinning: boolean;
    label: string;
  }) => (
    <button
      type="button"
      // Касание стрелки не уводит фокус из поля: клавиатура телефона остаётся.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      disabled={!ready || spinning}
      aria-label={label}
      title={label}
      aria-busy={spinning || undefined}
      className="grid h-11 w-11 flex-none place-items-center rounded-full disabled:cursor-default"
    >
      <span
        className={cn(
          'grid h-9 w-9 place-items-center rounded-full transition-colors',
          ready || spinning ? 'bg-accent text-fg-inv' : 'bg-line text-[#8a8a90]',
        )}
      >
        {spinning ? <Spinner className="h-[18px] w-[18px]" /> : <ArrowUpIcon className="h-[18px] w-[18px]" />}
      </span>
    </button>
  );

  // Компьютер. С открытым листом справа — «Создать» с подписью: цена, «Всё уже добавлено», «Выбери значение» и
  // «Проверяем…» иначе не видны. Лист свёрнут или ещё ищем — стрелка возвращает лист (как Enter).
  const desktopAction = showCreate ? (
    <motion.button
      key="create"
      type="button"
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.16, ease: [0.2, 0.9, 0.2, 1] }}
      onClick={() => (view === 'manual' ? addManual('click') : create('click'))}
      disabled={!enabled}
      aria-busy={creating || undefined}
      className={cn(
        'relative flex h-11 flex-none items-center whitespace-nowrap rounded-pill pl-5 text-[15px] font-semibold transition-colors',
        actionable ? 'hc-create-pad' : 'pr-5',
        creating
          ? 'cursor-wait bg-accent text-fg-inv'
          : enabled
            ? 'bg-accent text-fg-inv hover:bg-accent-hi'
            : 'cursor-default bg-line text-[#8a8a90]',
      )}
    >
      {/* Пока создаём, обычная подпись держит ширину, поверх — спиннер и «Создаём…». */}
      <span className={cn('flex items-center gap-2.5', creating && 'invisible')}>
        <span className="hc-wide-only">{wideLabel}</span>
        <span className="hc-narrow-only">{shortLabel}</span>
        {actionable && (
          <span
            aria-hidden
            className={cn(
              'hc-wide-only h-6 w-6 items-center justify-center rounded-[7px] font-mono text-[12px] font-medium',
              enabled ? 'bg-[rgba(20,20,22,0.16)]' : 'bg-[#34343a]',
            )}
          >
            ⏎
          </span>
        )}
      </span>
      {creating && (
        <span className="absolute inset-0 flex items-center justify-center gap-2">
          <Spinner className="h-[18px] w-[18px]" />
          {t('cardsWorkspace.manual.submitting')}
        </span>
      )}
    </motion.button>
  ) : (
    arrowButton({
      onClick: reopen,
      ready: !!text && !busy && !sheetVisible,
      spinning: !!text && lookup.status === 'loading',
      label: t('homeComposer.showAnswer'),
    })
  );

  const desktopRows = (
    <>
    <div ref={rowRef} className="hc-wrap relative z-20 h-[98px]">
      {cloud && (
        // Ширина — по тексту, не шире строки: с пределом 480 px «учить.» уходило одно на вторую строку (приёмка A1-11).
        <div
          role="status"
          className={cn(
            'pointer-events-none absolute left-0 w-max max-w-full rounded-[14px] border border-solid border-line bg-card px-3.5 py-2 text-[14px] leading-[1.4] text-muted [text-wrap:balance]',
            atTop ? 'top-[calc(100%+8px)]' : 'bottom-[calc(100%+8px)]',
          )}
        >
          {cloud}
        </div>
      )}
      {/* Сигнал — до карточки строки: поднявшийся лист в DOM позже и ложится поверх. */}
      {signalNode}
      <div
        data-tour="card"
        onKeyDown={onCardKeyDown}
        className={cn(
          'absolute inset-x-0 rounded-[24px] border border-solid bg-card transition-[border-color,box-shadow] duration-200',
          // Строка сверху — карточка растёт вниз, поверх страницы.
          atTop ? 'top-0' : 'bottom-0',
          editing ? 'hc-glow border-[#3a3a40]' : locked ? 'border-accent/25' : 'hc-accent border-accent/45',
        )}
      >
        <div
          className={cn(
            'hc-grid',
            atTop && 'hc-grid--top',
            sheetVisible && 'sheet-open',
            showCreate && 'has-create',
            manual !== null && 'is-manual',
          )}
        >
          <div
            className="hc-field-area flex h-12 min-w-0 items-center gap-2 px-5 pt-1"
            onPointerDown={emailLocked ? undefined : onFieldAreaPointerDown}
          >
            {/* Лупа — поле ищет слово в словаре (замечание владельца 2026-10-22: назначение строки видно сразу);
                в ручном вводе словаря нет — карандаш. */}
            {emailLocked ? (
              <LockIcon className="h-4 w-4 flex-none text-muted" aria-hidden />
            ) : manual !== null ? (
              <PencilIcon className="h-[18px] w-[18px] flex-none text-accent" />
            ) : (
              <SearchIcon className={cn('h-[18px] w-[18px] flex-none transition-colors', editing ? 'text-fg' : 'text-muted')} aria-hidden />
            )}
            <PlainField
              ref={fieldRef}
              id={fieldId}
              value={text}
              onChange={onTextChange}
              onKeyDown={onFieldKeyDown}
              onFocus={() => {
                setFocused(true);
                if (quietFocus.current) quietFocus.current = false;
                else reopen();
              }}
              onBlur={() => {
                setFocused(false);
                // Ушёл из поля — облачко о переиспользованной карточке прочитано (A2-03).
                setReusedNote(null);
              }}
              placeholder={
                emailLocked
                  ? t('homeComposer.lockedEmail')
                  : manual !== null
                    ? t('homeComposer.manualPlaceholder')
                    : t('homeComposer.placeholder')
              }
              disabled={emailLocked}
              ariaLabel={t('homeComposer.fieldAria')}
              ariaExpanded={sheetVisible}
              ariaControls={sheetId}
              lang="en"
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
              enterKeyHint="go"
              maxLength={100}
              // Набранное слово — не в записях сессий и не в аналитике (ТЗ § 16).
              className="hc-field amp-mask ym-hide-content ym-disable-keys py-2.5 text-[16px] leading-6 text-fg outline-none"
            />
            {manual !== null && (
              <>
                <span aria-hidden className="flex-none text-muted">
                  →
                </span>
                <PlainField
                  ref={translationRef}
                  value={translation}
                  onChange={setTranslation}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') addManual('enter');
                  }}
                  onFocus={() => setTranslationFocused(true)}
                  onBlur={() => setTranslationFocused(false)}
                  placeholder={t('cardsWorkspace.manual.translationPlaceholder')}
                  ariaLabel={t('cardsWorkspace.manual.translationLabel')}
                  maxLength={100}
                  className="hc-translation amp-mask ym-hide-content ym-disable-keys py-2.5 text-[16px] leading-6 text-fg outline-none"
                />
              </>
            )}
            {text && (
              <button
                type="button"
                onClick={clear}
                aria-label={t('cardsWorkspace.choose.clear')}
                className="grid h-11 w-11 flex-none place-items-center rounded-full text-muted transition-colors hover:text-fg"
              >
                <span className="grid h-8 w-8 place-items-center rounded-full bg-elev">
                  <XIcon className="h-3.5 w-3.5" />
                </span>
              </button>
            )}
            {statusText && <span className="min-w-0 truncate text-[14px] text-muted">{statusText}</span>}
          </div>

          <DictionarySheet
            id={sheetId}
            open={sheetVisible}
            maxHeight={metrics.maxSheet}
            busy={busy}
            view={view}
            word={manual ?? word}
            stale={stale !== null && answer === null}
            transcription={foundData?.transcription ?? null}
            yandex={foundData?.source === 'yandex'}
            choice={choice}
            price={price}
            errorReason={shown?.status === 'error' ? shown.reason : null}
            moduleTitle={mod.module?.title ?? null}
            notice={notice}
            exhaustedActions={exhaustedActions(enterManual)}
            onCollapse={() => (manual !== null ? exitManual() : collapse())}
            onRetry={lookup.retry}
            onManual={enterManual}
          />

          <div className="hc-actions relative flex h-12 min-w-0 items-center gap-2 pb-1 pl-2 pr-2">
            <ModuleChip
              ref={chipRef}
              className="max-w-[360px]"
              status={mod.status}
              module={mod.module}
              menuOpen={menuOpen}
              menuId={menuId}
              disabled={busy || emailLocked}
              onToggle={() => setMenuOpen((v) => !v)}
              onRetry={() => void modulesQuery.refetch()}
              onCreate={() => createModuleThen()}
            />
            {menuOpen && (
              <ModuleMenu
                id={menuId}
                modules={moduleList}
                recentIds={mod.recent.map((m) => m.id)}
                currentId={mod.module?.id ?? null}
                anchorRef={chipRef}
                onSelect={onSelectModule}
                onCreate={() => createModuleThen()}
                onClose={closeMenu}
              />
            )}
            <ManualChip active={manual !== null} disabled={busy || emailLocked} onToggle={toggleManual} />
            <div className="ml-auto flex flex-none items-center">{desktopAction}</div>
          </div>
        </div>
      </div>
    </div>
    {/* Над затемнением (z-20, как ряд): подбор по теме — часть строки, остаётся нажимаемым при фокусе в поле. Строка
        сверху — под открытым листом его нет: лист раскрывается вниз поверх. */}
    {/* Строка сверху: место пилюли держим и под открытым листом (invisible) — страница под полосой не прыгает. */}
    {!keyboardOpen && (
      <div
        className={cn('relative z-20 mt-2.5 flex h-9 w-max max-w-full', atTop && (sheetVisible || cloud || signal.view) && 'invisible')}
        aria-hidden={atTop && (sheetVisible || cloud || signal.view) ? true : undefined}
      >
        {topicsLink}
      </div>
    )}
    </>
  );

  // Телефон: стрелка открывает этап 2, как Enter и плашка превью (ТЗ § 7); у ручной карточки — добавляет её.
  const phoneAction =
    manual !== null
      ? arrowButton({
          onClick: () => addManual('click'),
          ready: manualReady,
          spinning: creating || flowChecking,
          label: flowChecking ? t('cardsWorkspace.choose.checking') : t('homeComposer.addCard'),
        })
      : arrowButton({
          onClick: () => openChoose('enter'),
          ready: !!text && foundCurrent && !choice.allLocked && !busy,
          spinning: !!text && lookup.status === 'loading',
          label: t('homeComposer.chooseAria'),
        });

  // Телефон: сверху поле со ✕, снизу чип модуля и кнопка (макет B). Карточка стоит абсолютно и растёт вверх
  // (ручная карточка — на ряд перевода); над ней облачко или превью.
  // Телефон, строка сверху: фокус в поле поднимает строку в самый верх, под верхнюю полосу (владелец 2026-10-28: «в
  // мобилке нужно наверх перемещать её, в самый верх при фокусе») — превью и клавиатуре остаётся весь экран. Одна
  // прокрутка `main` до липкого положения полосы; уже наверху или некуда крутить — ничего.
  const liftToTop = () => {
    if (!atTop || !isMobile) return;
    const dock = dockRef.current;
    const main = dock?.closest('main');
    const row = rowRef.current;
    if (!dock || !main || !row) return;
    // Карточка встаёт в 8 px от верха экрана — на верхнюю полосу с логотипом («на логотип, а не под»).
    const offset = row.getBoundingClientRect().top - dock.getBoundingClientRect().top;
    const nextTop = Math.round(8 - offset);
    setLiftTop(nextTop);
    const delta = dock.getBoundingClientRect().top - main.getBoundingClientRect().top - nextTop;
    if (delta <= 1) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    main.scrollTo({ top: main.scrollTop + delta, behavior: reduce ? 'auto' : 'smooth' });
  };

  const phoneRows = (
    <>
    {/* Ряд и так свой контекст наложения (relative z-20), а fixed-слоёв внутри нет — меню модулей и этап 2 вне полосы. */}
    <div ref={rowRef} className="hc-prow relative z-20 h-[98px]">
      <div
        data-tour="card"
        onKeyDown={onCardKeyDown}
        className={cn(
          'absolute inset-x-0 rounded-[24px] border border-solid bg-card px-2 py-1 transition-[border-color,box-shadow] duration-200',
          atTop ? 'top-0' : 'bottom-0',
          editing ? 'hc-glow border-[#3a3a40]' : locked ? 'border-accent/25' : 'hc-accent border-accent/45',
          flashing && 'hc-flash',
        )}
      >
        {phoneCloud && (
          <div
            role="status"
            // balance: перенос без висящего последнего слова (приёмка A1-11). break-word — запасной выход, если
            // неразрывное короткое имя модуля из широких букв всё же не встанет в строку (R2-10).
            className={cn(
              'pointer-events-none absolute -left-px max-w-[calc(100%+2px)] rounded-[14px] border border-solid border-line bg-card px-3.5 py-2 text-[14px] leading-[1.4] [overflow-wrap:break-word] [text-wrap:balance]',
              atTop ? 'top-[calc(100%+9px)]' : 'bottom-[calc(100%+9px)]',
              phoneCloud.tone === 'danger' ? 'text-danger' : 'text-muted',
            )}
          >
            {phoneCloud.text}
          </div>
        )}
        {/* Сигнал — над полем во всю его ширину, там же, где облачко; превью в DOM позже и ложится поверх. */}
        {signalNode}
        {/* Превью — по ширине поля, на 8 px выше его рамки (строка сверху — на 8 px ниже). */}
        <div className={cn('absolute -inset-x-px', atTop ? 'top-[calc(100%+1px)]' : 'bottom-[calc(100%+1px)]')}>
          <PhonePreview
            id={sheetId}
            below={atTop}
            open={previewVisible}
            answer={previewAnswer}
            stale={stale !== null && answer === null}
            word={word}
            maxHeight={previewRoom}
            errorText={previewError ? t(previewError.key) : ''}
            onRetry={previewError?.retry ? lookup.retry : undefined}
            note={
              quotaOut ? (
                <span className="flex items-start gap-1.5 text-[14px] leading-[1.4] text-warn">
                  <TokenIcon size={14} className="mt-[3px]" />
                  {t('homeComposer.quotaOutPhone')}
                </span>
              ) : undefined
            }
            onChoose={() => openChoose('plaque')}
            onManual={enterManual}
          />
        </div>
        <div
          className="flex h-11 items-center pl-3"
          onPointerDown={emailLocked ? undefined : onPhoneRowPointerDown}
          onClick={emailLocked ? undefined : onPhoneRowClick}
        >
          {emailLocked ? (
            <LockIcon className="mr-2 h-4 w-4 flex-none text-muted" aria-hidden />
          ) : manual !== null ? (
            <PencilIcon className="mr-2 h-[18px] w-[18px] flex-none text-accent" />
          ) : (
            <SearchIcon className={cn('mr-2 h-[18px] w-[18px] flex-none transition-colors', editing ? 'text-fg' : 'text-muted')} aria-hidden />
          )}
          <PlainField
            ref={fieldRef}
            id={fieldId}
            value={text}
            onChange={onTextChange}
            onKeyDown={onFieldKeyDown}
            onFocus={() => {
              setFocused(true);
              liftToTop();
              if (quietFocus.current) quietFocus.current = false;
              else reopen();
            }}
            onBlur={() => {
              setFocused(false);
              // Ушёл из поля — облачко о переиспользованной карточке прочитано (A2-03).
              setReusedNote(null);
            }}
            placeholder={
              emailLocked
                ? t(wideField ? 'homeComposer.lockedEmail' : 'homeComposer.lockedEmailShort')
                : manual !== null
                  ? t(wideField ? 'homeComposer.manualPlaceholder' : 'homeComposer.manualPlaceholderShort')
                  : wideField
                    ? t('homeComposer.placeholder')
                    : t('homeComposer.placeholderShort')
            }
            disabled={emailLocked}
            ariaLabel={t('homeComposer.fieldAria')}
            ariaExpanded={previewVisible}
            ariaControls={sheetId}
            lang="en"
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            enterKeyHint="go"
            maxLength={100}
            // 16 px: мельче iOS зумит поле при фокусе (ТЗ § 5).
            className={cn(
              'hc-pfield amp-mask ym-hide-content ym-disable-keys min-w-0 flex-1 py-2.5 text-[16px] leading-6 text-fg outline-none',
              emailLocked && 'hc-locked',
            )}
          />
          {text && (
            <button
              type="button"
              // Касание ✕ не уводит фокус из поля: клавиатура остаётся.
              onMouseDown={(event) => event.preventDefault()}
              onClick={clear}
              aria-label={t('cardsWorkspace.choose.clear')}
              className="grid h-11 w-11 flex-none place-items-center rounded-full text-muted"
            >
              <span className="grid h-8 w-8 place-items-center rounded-full bg-elev">
                <XIcon className="h-3.5 w-3.5" />
              </span>
            </button>
          )}
        </div>
        {manual !== null && (
          <div className="flex h-11 items-center gap-2 border-t border-solid border-line pl-3 pr-1">
            <span className="flex-none text-[12px] font-semibold uppercase tracking-[0.14em] text-muted">
              {t('cardsWorkspace.manual.translationLabel')}
            </span>
            <PlainField
              ref={translationRef}
              value={translation}
              onChange={setTranslation}
              onKeyDown={(event) => {
                if (event.key === 'Enter') addManual('enter');
              }}
              onFocus={() => {
                setTranslationFocused(true);
                liftToTop();
              }}
              onBlur={() => setTranslationFocused(false)}
              placeholder={t('cardsWorkspace.manual.translationPlaceholder')}
              ariaLabel={t('cardsWorkspace.manual.translationLabel')}
              enterKeyHint="go"
              maxLength={100}
              className="hc-ptranslation amp-mask ym-hide-content ym-disable-keys min-w-0 flex-1 py-2.5 text-[16px] leading-6 text-fg outline-none"
            />
          </div>
        )}
        <div className="flex h-11 min-w-0 items-center gap-2">
          <ModuleChip
            ref={chipRef}
            phone
            status={mod.status}
            module={mod.module}
            menuOpen={moduleMenuOpen}
            menuId={menuId}
            disabled={busy || emailLocked}
            onToggle={openModuleMenu}
            onRetry={() => void modulesQuery.refetch()}
            onCreate={() => createModuleThen()}
          />
          <ManualChip active={manual !== null} disabled={busy || emailLocked} onToggle={toggleManual} />
          <div className="ml-auto flex flex-none items-center">{phoneAction}</div>
        </div>
      </div>
    </div>
    {/* Строка сверху растёт вниз (превью, ряд перевода ручной карточки, облачко) — подбор по теме под ней не
        показываем, иначе карточка на него наезжает. */}
    {!keyboardOpen && (
      <div
        className={cn(
          'relative z-20 flex h-11 w-max max-w-full items-center',
          atTop && (previewVisible || manual !== null || phoneCloud || signal.view) && 'invisible',
        )}
        aria-hidden={atTop && (previewVisible || manual !== null || phoneCloud || signal.view) ? true : undefined}
      >
        {topicsLink}
      </div>
    )}
    </>
  );

  return (
    <MotionConfig reducedMotion="user">
      {/* Полоса — sticky, в потоке постоянной высоты (ТЗ § 3, § 5): с открытой клавиатурой видна только
          строка, а место в потоке прежнее, поэтому после закрытия клавиатуры прокрутка главной та же.
          Сама полоса прозрачна для касаний — их ловят только её части. */}
      <section
        ref={dockRef}
        data-dock
        aria-label={t('homeComposer.label')}
        className={cn(
          // Телефон, строка сверху: в покое ниже верхней полосы (z-30) — заголовок «Создать карточку» уходит под неё; в
          // работе — поверх полосы, на месте логотипа.
          'pointer-events-none sticky flex w-full flex-col',
          atTop && isMobile && !phoneLifted ? 'z-[25]' : 'z-[45]',
          // Строка сверху — липнет под верхней полосой телефона (56 px) или к верху main; клавиатура её не двигает.
          // Заголовок «Создать карточку» при прокрутке уезжает вверх (отрицательный top на его высоту): липнет сама строка.
          // При этом над прилипшей строкой — 8 px воздуха (телефон: под верхней полосой 56 px).
          atTop ? cn('justify-start', isMobile ? !phoneLifted && 'top-[26px]' : 'top-[-34px]') : 'bottom-0 justify-end',
          !atTop && (isMobile ? 'hc-dock-phone' : 'hc-dock-desk'),
        )}
        data-dock-placement={atTop ? 'top' : 'bottom'}
        style={
          phoneLifted ? { top: liftTop } : !atTop && inset ? { transform: `translateY(-${inset}px)` } : undefined
        }
      >
        {/* Экранный диктор: начало и итог нажатий этого захода — по одному разу, виден сигнал или нет
            (ТЗ § 8, § 16). Сам сигнал и записи списка не live: одно событие не звучит дважды. top-0 left-0: без них
            absolute-элемент во flex-колонке с justify-end встаёт статично под низ полосы, и короткая главная
            прокручивалась на 1 px (приёмка A1-08). */}
        <p className="sr-only left-0 top-0" aria-live="polite">
          {signal.live}
        </p>
        {/* Затемнение листа (компьютер, 0,5) и превью (телефон, 0,55) — внутри полосы на всю высоту main:
            боковая панель не затемняется, гамбургер и баланс телефона — затемняются (ТЗ § 4). */}
        <div
          aria-hidden
          className={cn(
            'hc-dim z-10',
            // Телефон, строка сверху — на весь экран: строка в работе поднята на верхнюю полосу, и замер «от полосы до
            // main» во время её подъёма врал бы. Компьютер — от верха main вниз (боковая панель не затемняется).
            atTop && isMobile ? 'fixed inset-0' : cn('absolute inset-x-0', !atTop && 'bottom-0'),
            isMobile ? 'hc-dim-phone bg-black/55' : 'bg-[rgba(10,10,12,0.5)]',
            (dimOn || focusDim) && 'is-on',
          )}
          style={
            atTop && isMobile
              ? undefined
              : atTop
                ? { top: -metrics.dockOffset, height: metrics.mainHeight || '100vh' }
                : { height: metrics.mainHeight || '100vh' }
          }
          onPointerDown={() => {
            dimDownWithMenu.current = menuOpenRef.current || menuClosedOutside.current;
          }}
          onClick={() => {
            if (dimDownWithMenu.current) {
              dimDownWithMenu.current = false;
              return;
            }
            if (isMobile) {
              closePreview();
              return;
            }
            // Клик по затемнению — выход из строки: лист сворачивается (слово и отметки остаются), фокус уходит,
            // затемнение гаснет. Вернуться — клик в поле (лист поднимется снова).
            if (sheetVisible) {
              if (manual !== null) {
                setManual(null);
                setTranslation('');
              }
              setCollapsed(true);
              setOpen(false);
              setMenuOpen(false);
            }
            leaveField();
          }}
        />
        <div
          className={cn(
            'pointer-events-auto relative',
            atTop ? 'bg-base' : isMobile ? 'border-t border-solid border-line bg-[#0e0e10]' : 'bg-base',
          )}
        >
          {/* Растворение у полосы: прокрученный текст не обрывается резко; клики не ловит. Полоса внизу (компьютер) —
              над ней, полоса сверху — под ней. */}
          {atTop ? (
            <div aria-hidden className="pointer-events-none absolute inset-x-0 top-full h-4 bg-gradient-to-b from-base to-transparent" />
          ) : (
            !isMobile && (
              <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-full h-4 bg-gradient-to-b from-transparent to-base" />
            )
          )}
          <div
            className={cn(
              'px-4 sm:px-6',
              atTop ? cn('pb-3 pt-2', !isMobile && 'nav:px-12') : cn('pt-2', !isMobile && cn('nav:px-12', keyboardOpen ? 'pb-2' : 'hc-dock-pad')),
            )}
            style={isMobile && !atTop ? { paddingBottom: keyboardOpen ? 8 : 'max(8px, env(safe-area-inset-bottom))' } : undefined}
          >
            {/* Ширина — как у колонки страницы (1200 px с 2026-10-22). */}
            <div className="mx-auto max-w-[1200px]">
              {heading && (
                <h2
                  className={cn(
                    'relative z-20 mb-2 flex items-center gap-2 font-serif font-medium text-fg transition-opacity duration-150',
                    isMobile ? 'text-[17px] leading-[22px]' : 'text-[20px] leading-[26px]',
                    (editing || sheetVisible || keyboardOpen) && 'invisible opacity-0',
                  )}
                >
                  <span aria-hidden className="grid h-6 w-6 flex-none place-items-center rounded-full bg-accent/15 text-accent">
                    <PlusIcon className="h-3.5 w-3.5" />
                  </span>
                  {heading}
                </h2>
              )}
              {isMobile ? phoneRows : desktopRows}
            </div>
          </div>
        </div>
      </section>

      {isMobile &&
        createPortal(
          <AnimatePresence>
            {chooseVisible && (
              <motion.div
                key="choose-backdrop"
                aria-hidden
                className="fixed inset-0 z-[80] bg-black/40"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.24, ease: 'easeOut' }}
              />
            )}
            {chooseVisible && choiceData && (
              <ChooseTranslations
                key="choose"
                word={chooseWord || word}
                data={choiceData}
                choice={choice}
                submitting={creating}
                onBack={() => layers.close('choose')}
                onCreate={() => create('click')}
                moduleSlot={
                  <ModuleSlot
                    status={mod.status}
                    module={mod.module}
                    menuOpen={moduleMenuOpen}
                    menuId={menuId}
                    disabled={busy}
                    onOpen={openModuleMenu}
                    onRetry={() => void modulesQuery.refetch()}
                    onCreate={() => createModuleThen()}
                  />
                }
                notice={chooseNotice}
                noticeAction={chooseNoticeAction}
                pending={choosePending}
                createDisabled={chooseCreateDisabled}
                exhaustedActions={exhaustedActions(manualFromChoose)}
              />
            )}
          </AnimatePresence>,
          document.body,
        )}

      <AnimatePresence>
        {isMobile && moduleMenuOpen && (
          <ModuleMenu
            key="modules"
            variant="sheet"
            id={menuId}
            modules={moduleList}
            recentIds={mod.recent.map((m) => m.id)}
            currentId={mod.module?.id ?? null}
            onSelect={onSelectModule}
            onCreate={() => createModuleThen()}
            onClose={closeMenu}
          />
        )}
      </AnimatePresence>
    </MotionConfig>
  );
});
